import { createSelector } from 'reselect';

import {
    Banner,
    ConversationWithMetadata,
    Link,
    LocationState,
    Message,
    MessageWithMetadata,
    RootState,
    UsersState,
    VideoState,
    VisitorState,
    SettingsState,
} from '../typedefs/store';
import { getSettings } from './settings';

const rootMessages = (state: RootState) => state.message.messages;
export const getVisitor = (state: RootState) => state.visitor;
export const getLocation = (state: RootState) => state.location;
const rootTeams = (state: RootState) => state.teams;
const rootConvo = (state: RootState) => state.conversation;
const getUsers = (state: RootState) => state.users;
export const getVideo = (state: RootState) => state.video;
export const getUi = (state: RootState) => state.ui;

export const getConvo = createSelector(
    rootConvo,
    getLocation,
    getUsers,
    (convo, location, users): ConversationWithMetadata => {
        let extendedConvo;
        if (convo.userIds.length > 0 && users[convo.userIds[0]]) {
            extendedConvo = {
                ...convo,
                avatar: users[convo.userIds[0]].avatar,
                users: convo.userIds.map(userId => users[userId]),
            };
        } else {
            extendedConvo = { ...convo, avatar: location.avatar, users: [] };
        }

        // if someone is typing, set the appropriate avatar
        let typingAvatar;
        if (convo.typingStatus.active) {
            if (convo.typingStatus.userId && users[convo.typingStatus.userId]) {
                typingAvatar = users[convo.typingStatus.userId].avatar;
            } else {
                typingAvatar = location.avatar;
            }
        }

        return {
            ...extendedConvo,
            typingStatus: { ...extendedConvo.typingStatus, avatar: typingAvatar },
        };
    },
);

export const getMessages = createSelector(
    rootMessages,
    getConvo,
    getLocation,
    getUsers,
    getVisitor,
    (messages, convo, location, users, visitor): MessageWithMetadata[] => {
        return messages
            .sort(sortById)
            .sort(sortByDate)
            .filter(message => message.type !== 'visitor-timeout')
            .map(message => {
                const avatar = calculateAvatar(message, users, location);
                const readByMe = calculateReadByMe(message, visitor);

                return { ...message, avatar, readByMe };
            });
    },
);

export const getNumUnreadMessages = createSelector(getMessages, messages => {
    return messages.filter(message => message.category === 'message' && !message.readByMe).length;
});

export const hasAnyVisitorMessages = createSelector(
    rootMessages,
    messages => messages.filter(message => message.fromType === 'visitor').length > 0,
);

function sortByDate(a: Message, b: Message) {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    return dateA.getTime() - dateB.getTime();
}

function sortById(a: MessageWithMetadata, b: MessageWithMetadata) {
    if (typeof a.id !== 'number') return 1; // id could be a string (tempId is used while message is sending)
    if (typeof b.id !== 'number') return -1; // id could be a string (tempId is used while message is sending)
    return a.id - b.id;
}

function calculateReadByMe(message: Message, visitor: VisitorState): boolean {
    if (message.fromType === 'visitor' || message.fromType === 'bot') {
        return true;
    } else {
        if (message.readReceipts) {
            const myReadReceipts = message.readReceipts.filter(
                readReceipt => readReceipt.visitorId === visitor.visitorId,
            );

            return myReadReceipts.length > 0;
        }
    }

    return false;
}

function calculateAvatar(
    message: Message,
    users: UsersState,
    location: LocationState,
): string | undefined {
    if (message.fromType === 'agent' && message.fromId && users[message.fromId]) {
        return users[message.fromId].avatar;
    } else if (message.fromType === 'bot') {
        return location.avatar;
    }

    return;
}

export const getTeams = createSelector(rootTeams, teams => {
    const teamArray = [];
    for (var key in teams) {
        if (teams.hasOwnProperty(key)) {
            teamArray.push(teams[key]);
        }
    }
    return teamArray;
});

export const hasRecentVideoRequest = createSelector(
    getVideo,
    rootMessages,
    (video: VideoState, messages: Message[]) => {
        // is there a video request?
        if (!video.request) return false;

        // has it already been accepted?
        if (video.request.accepted === true) return false;

        // is it from within the last minute?
        const now = new Date();
        const requestTimestamp = new Date(video.request.timestamp);
        const msDiff = Math.abs(requestTimestamp.getTime() - now.getTime());
        if (msDiff > 60000) return false;

        return true;
    },
);

export const shouldShowVideoPanel = createSelector(
    getVideo,
    (video: VideoState): boolean => !!(video.request && video.request.accepted === true),
);

export const getStarters = createSelector(getSettings, (settings: SettingsState): string[] => {
    // if starters are passed via inline config, then use them
    if (settings.inlineConfig.starters) return settings.inlineConfig.starters;

    // if starter comes from API/location settings, then use them
    if (settings.starters && settings.starters.length > 0) return settings.starters;

    // fall back to a default if there are none
    return ['We are available to answer your questions now!'];
});

export const getBanners = createSelector(getSettings, (settings: SettingsState): Banner[] => {
    // if banners are passed via inline config, then use them
    if (settings.inlineConfig.banners) {
        return settings.inlineConfig.banners.map(banner => {
            return { banner: banner.text, url: banner.url };
        });
    }
    // if banners comes from API/location settings, then use them
    if (settings.banners && settings.banners.length > 0) return settings.banners;

    // fall back to a empty array if there are none
    return [];
});

export const getLinks = createSelector(getSettings, (settings): Link[] => {
    // if links are passed via inline config, then use them
    if (settings.inlineConfig.links) return settings.inlineConfig.links;

    // if links comes from API/location settings, then use them
    if (settings.links && settings.links.length > 0) return settings.links;

    // fall back to a empty array if there are none
    return [];
});

export const hasLinks = createSelector(getLinks, (links): boolean => links.length > 0);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/selectors/conversation.ts